import {
  ArticlesMessage,
  AttachmentConversationMessage,
  Conversation,
  ConversationArticle,
  ConversationLogItemMessageAdded,
  ConversationParticipant,
  InlineDataConversationMessage,
  OptionsMessage,
  RichTextConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';

export const buildTextTranscriptLine = (
  conversation: Conversation,
  participant: ConversationParticipant,
  message: TextConversationMessage,
): string => {
  return `${participant.displayName}: ${message.text}\n`;
};

export const buildAttachmentTranscriptLine = (
  conversation: Conversation,
  participant: ConversationParticipant,
  message: AttachmentConversationMessage,
): string => {
  const attachmentName =
    message.attachment.name || message.attachment.file?.name;
  return `${participant.displayName}: Added attachment ${attachmentName}\n`;
};

export const buildOptionsTranscriptLine = (
  conversation: Conversation,
  participant: ConversationParticipant,
  message: OptionsMessage,
): string => {
  const option = message.options.find((o) => o.id === message.selectedOptionId);
  let line = `${participant.displayName}: `;
  if (option) {
    line += `Selected option ${option.label}`;
  } else {
    line += `Was presented options ${message.options
      .map((o) => o.label)
      .join(', ')}`;
  }
  if (message.purpose) {
    line += ` for purpose ${message.purpose}`;
  }
  return `${line}\n`;
};

export const buildArticlesTranscriptLine = (
  conversation: Conversation,
  participant: ConversationParticipant,
  message: ArticlesMessage,
): string => {
  const uhContext = conversation.context.universalHelp as {
    articlesVisited?: Record<string, ConversationArticle>;
  };
  const articlesVisited = uhContext?.articlesVisited;
  return `${participant.displayName}: Was presented articles: ${message.articles
    .map((article) => {
      const visited = articlesVisited && articlesVisited[article.id];
      return `${article.url}${visited ? ' (visited)' : ''}`;
    })
    .join(', ')}\n`;
};

export const buildInlineDataTranscriptLine = (
  participant: ConversationParticipant,
  message: InlineDataConversationMessage,
): string => {
  let inlineDataTranscript = '';
  const inlineDataName = message.inlineData.name;
  const inlineDataValue = Array.isArray(message.inlineData.data)
    ? (message.inlineData.data[0]?.value as object)
    : undefined;
  if (!inlineDataValue) {
    return `${participant.displayName}: * InlineData ${inlineDataName} without value. *\n`;
  }

  switch (inlineDataName) {
    case 'universal_help_sign_in_prompt':
      inlineDataTranscript = 'User prompted to sign in. ';
      if ('merchantName' in inlineDataValue && inlineDataValue.merchantName) {
        inlineDataTranscript += `User signed in as ${inlineDataValue.merchantName}.`;
      } else {
        inlineDataTranscript += 'User could not sign in.';
      }
      break;
    case 'email_verification':
      inlineDataTranscript = 'User prompted to verify their email. ';
      if ('selected_option_id' in inlineDataValue) {
        if (inlineDataValue.selected_option_id === 'noaccess') {
          if (
            'email_send_count' in inlineDataValue &&
            (inlineDataValue.email_send_count as number) > 1
          ) {
            inlineDataTranscript +=
              "User didn't recieve the verification email.";
          } else {
            inlineDataTranscript += "User doesn't have access to the email.";
          }
        } else if (inlineDataValue.selected_option_id === 'resend') {
          inlineDataTranscript +=
            'User requested to resend email verification.';
        }
      }
      break;
    case 'merchant_related_case':
      inlineDataTranscript = 'User shown related case.';
      if ('related_case' in inlineDataValue) {
        inlineDataTranscript += `Case: ${inlineDataValue.related_case}.`;
      }
      break;
    default:
      inlineDataTranscript = `Unsuported transcript for inline data ${inlineDataName}.`;
  }
  return `${participant.displayName}: ${inlineDataTranscript}\n`;
};

export const buildRichTextTranscriptLine = (
  conversation: Conversation,
  participant: ConversationParticipant,
  message: RichTextConversationMessage,
): string => {
  let line = `${participant.displayName}: ${message.text}\n\n`;

  switch (message.data.type) {
    case 'button-options-conversation-message':
    case 'select-options-conversation-message':
      line += buildOptionsTranscriptLine(
        conversation,
        participant,
        message.data,
      );
      break;
    case 'articles-conversation-message':
      line += buildArticlesTranscriptLine(
        conversation,
        participant,
        message.data,
      );
      break;
    default:
    // unsupported transcript message type
  }

  return line;
};

export const buildTranscript = (conversation: Conversation): string => {
  const lines: string[] = [];

  const log = conversation.log.get();
  log
    .filter((item) => item.name === 'conversation-message-added')
    .forEach((item) => {
      const {message} = item as ConversationLogItemMessageAdded;
      const {participant} = message;

      switch (message.type) {
        case 'text-conversation-message':
          lines.push(
            buildTextTranscriptLine(conversation, participant, message),
          );
          break;
        case 'attachment-conversation-message':
          lines.push(
            buildAttachmentTranscriptLine(conversation, participant, message),
          );
          break;
        case 'button-options-conversation-message':
        case 'select-options-conversation-message':
          lines.push(
            buildOptionsTranscriptLine(conversation, participant, message),
          );
          break;
        case 'articles-conversation-message':
          lines.push(
            buildArticlesTranscriptLine(conversation, participant, message),
          );
          break;
        case 'rich-text-conversation-message':
          lines.push(
            buildRichTextTranscriptLine(conversation, participant, message),
          );
          break;
        case 'inline-data-conversation-message':
          lines.push(buildInlineDataTranscriptLine(participant, message));
          break;
        default:
        // unsupported transcript message type
      }
    });

  return lines.join('\n');
};
